/**
 * Clip Timeline Component
 * Real-time visualization of clip measurements over time
 */

import React, { useMemo, useState, useRef, useCallback, useLayoutEffect } from 'react';
import { ParentSize } from '@visx/responsive';
import { Group } from '@visx/group';
import { scaleTime, scaleLinear } from '@visx/scale';
import { LinePath, Circle, AreaClosed } from '@visx/shape';
import { curveMonotoneX } from '@visx/curve';
import { GridRows } from '@visx/grid';
import { AxisLeft, AxisBottom, AxisRight } from '@visx/axis';
import { useTooltip, TooltipWithBounds, defaultStyles } from '@visx/tooltip';
import { localPoint } from '@visx/event';
import { Brush } from '@visx/brush';
import { Bounds } from '@visx/brush/lib/types';
import BaseBrush from '@visx/brush/lib/BaseBrush';
import { toPng } from 'html-to-image';
import { ClipDataPoint, LiquidAddition, Annotation } from '../../lib/api-clip-monitor';
import { DiaperModel } from '../../lib/types';
import { calculateWetnessVolume, calculatePolynomialSlope } from '../../lib/utils';
import { format } from 'date-fns';
import { useClipDataStore } from '../../stores/clipDataStore';

export interface AlarmEvent {
  timestamp: Date;
  reason: string;
  hypothetical?: boolean;
}

export interface BackendTimelineEvent {
  timestamp: Date;
  type: 'emiction' | 'wet_alert' | 'leak_alert' | 'pool_alert' | 'pool_detected' | 'leak_detected' | 'pool_cleared';
  label: string;
  detail?: string;
}

interface ClipTimelineProps {
  data: ClipDataPoint[];
  diaperModel: DiaperModel | null;
  changeNeededPercent?: number | null;
  brushSelection?: { startTime: Date | null; endTime: Date | null };
  onBrushChange?: (startTime: Date | null, endTime: Date | null) => void;
  useDynamicRange?: boolean;
  liquidAdditions?: LiquidAddition[];
  annotations?: Annotation[];
  alarmEvents?: AlarmEvent[];
  backendEvents?: BackendTimelineEvent[];
  stabilizationPeriodSec?: number | null;
}

interface DataPoint {
  timestamp: Date;
  sourceTimestamp: string;
  wetnessLevel: number;
  volume: number;
  statusCode: number;
  batteryPercent: number;
  adcValues: number[];
}

const tooltipStyles = {
  ...defaultStyles,
  backgroundColor: 'rgba(0, 0, 0, 0.9)',
  color: 'white',
  padding: '10px 14px',
  borderRadius: '6px',
  fontSize: '12px',
  lineHeight: '1.5',
  pointerEvents: 'none' as const,
};

const StatusColors: Record<number, string> = {
  0: '#22c55e', // green - normal
  1: '#9ca3af', // gray - detached
  2: '#eab308', // yellow - shorted
  3: '#3b82f6', // blue - prev wet
  4: '#2563eb', // blue - new wet
  5: '#dc2626', // red - leak imminent
  6: '#9ca3af', // gray - identifying
  7: '#a855f7', // purple - reboot wet
  8: '#a855f7', // purple - reboot recovered
  9: '#6366f1', // indigo - sleep mode
  30: '#f97316', // orange - pooling
  255: '#a855f7', // purple - reboot dry (-1 as unsigned byte)
  '-1': '#a855f7', // purple - reboot dry
};

// Default color for unknown/unmapped status codes
const UNKNOWN_STATUS_COLOR = '#dc2626'; // red

const LIQUID_EXPECTED_COLOR = '#60a5fa';
const LIQUID_PATTERN_FILL = '#bfdbfe';
const LIQUID_DASH_ARRAY = '4,4';

// Status code labels aligned with BleAd.java and insert_packet.sql
// Lifecycle role: OPEN = backend opens new diaper, CLOSE = backend closes diaper, NEUTRAL = no lifecycle change
const StatusLabels: Record<number, string> = {
  0: 'Attached (Normal)',        // OPEN — backend opens diaper
  1: 'Detached',                 // CLOSE — backend closes diaper
  2: 'Shorted',                  // NEUTRAL — attached but no new diaper, wetness=NULL
  3: 'Same Wet Reattached',      // OPEN — backend opens diaper
  4: 'Wet Diaper Attached',      // OPEN — backend opens diaper
  5: 'Leak Imminent',            // OPEN — backend opens diaper
  6: 'Identifying',              // CLOSE — backend closes diaper (firmware scanning for type)
  7: 'Reboot Wet',               // NEUTRAL — no lifecycle change
  8: 'Reboot Recovered',         // NEUTRAL — no lifecycle change
  9: 'Entering Sleep',           // NEUTRAL — no lifecycle change (backend does NOT close)
  30: 'Pooling',                 // OPEN — backend opens diaper
  255: 'Reboot Dry',             // NEUTRAL — no lifecycle change
  '-1': 'Reboot Dry',
};

const getStatusLabel = (statusCode: number): string => {
  return StatusLabels[statusCode] || `Unknown (${statusCode})`;
};

const ClipTimelineChart: React.FC<ClipTimelineProps & {
  width: number;
  height: number;
  onMeasureOverhead?: (value: number) => void;
  hoveredTimestamp?: string | null;
  onHoverMeasurement?: (timestamp: string) => void;
  onLeaveHover?: () => void;
}> = React.memo(
  ({
    data,
    diaperModel,
    changeNeededPercent = 15,
    brushSelection,
    onBrushChange,
    useDynamicRange = false,
    liquidAdditions = [],
    annotations = [],
    alarmEvents = [],
    backendEvents = [],
    stabilizationPeriodSec,
    width: parentWidth,
    height: parentHeight,
    onMeasureOverhead,
    hoveredTimestamp,
    onHoverMeasurement,
    onLeaveHover,
  }) => {
    const chartRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const brushRef = useRef<BaseBrush | null>(null);
    const isUpdatingBrushProgrammatically = useRef(false);
    const skipNextBrushSync = useRef(false);
    const [hoveredPoint, setHoveredPoint] = useState<DataPoint | null>(null);

    const {
      showTooltip,
      hideTooltip,
      tooltipOpen,
      tooltipData,
      tooltipLeft = 0,
      tooltipTop = 0,
    } = useTooltip<DataPoint>();

    // Chart dimensions -- top margin grows to accommodate staggered alarm labels
    const width = parentWidth || 800;
    const height = parentHeight || 400;
    const alarmLabelSpace = Math.max(0, (alarmEvents?.length || 0)) * 14 + 20;
    const marginTop = Math.max(40, alarmLabelSpace);
    const margin = { top: marginTop, right: 80, bottom: 60, left: 80 };
    const innerWidth = Math.max(0, width - margin.left - margin.right);
    const innerHeight = Math.max(0, height - margin.top - margin.bottom);

    // Brush (overview) dimensions
    const brushHeight = 60;
    const brushMargin = { top: 10, right: 80, bottom: 20, left: 80 };
    const brushInnerWidth = Math.max(0, width - brushMargin.left - brushMargin.right);
    const brushInnerHeight = Math.max(0, brushHeight - brushMargin.top - brushMargin.bottom);

    // Transform all data (for overview and potential filtering)
    const allChartData = useMemo<DataPoint[]>(() => {
      return data.map((record) => {
        const volumeData = diaperModel
          ? calculateWetnessVolume(record.parsed.wetnessLevel, diaperModel)
          : { volume: 0 };

        const ts = new Date(record.timestamp);
        return {
          timestamp: ts,
          sourceTimestamp: ts.toISOString(),
          wetnessLevel: record.parsed.wetnessLevel,
          volume: volumeData.volume || 0,
          statusCode: record.parsed.statusCode,
          batteryPercent: record.parsed.batteryPercent,
          adcValues: record.parsed.adcValues,
        };
      });
    }, [data, diaperModel]);

    // Pre-compute stabilization tracking for pooling and change-needed events
    const stabilizationMap = useMemo<Record<string, {
      consecutiveSec: number;
      requiredSec: number;
      met: boolean;
      streakStartTime: Date;
      type: string;
    }>>(() => {
      const map: Record<string, { consecutiveSec: number; requiredSec: number; met: boolean; streakStartTime: Date; type: string }> = {};
      const stabSec = stabilizationPeriodSec || 0;
      if (stabSec <= 0 || allChartData.length === 0) return map;

      let poolingStreakStart: Date | null = null;
      let changeNeededStreakStart: Date | null = null;
      const changeNeededVolume = diaperModel && changeNeededPercent
        ? diaperModel.capacity * (changeNeededPercent / 100)
        : null;

      for (const d of allChartData) {
        if (d.statusCode === 30) {
          if (!poolingStreakStart) poolingStreakStart = d.timestamp;
          const elapsed = (d.timestamp.getTime() - poolingStreakStart.getTime()) / 1000;
          map[d.sourceTimestamp] = {
            consecutiveSec: elapsed,
            requiredSec: stabSec,
            met: elapsed >= stabSec,
            streakStartTime: poolingStreakStart,
            type: 'Pooling',
          };
        } else {
          poolingStreakStart = null;
        }

        if (changeNeededVolume != null && d.volume >= changeNeededVolume) {
          if (!changeNeededStreakStart) changeNeededStreakStart = d.timestamp;
          const elapsed = (d.timestamp.getTime() - changeNeededStreakStart.getTime()) / 1000;
          map[d.sourceTimestamp + ':cn'] = {
            consecutiveSec: elapsed,
            requiredSec: stabSec,
            met: elapsed >= stabSec,
            streakStartTime: changeNeededStreakStart,
            type: 'Change Needed',
          };
        } else {
          changeNeededStreakStart = null;
        }
      }
      return map;
    }, [allChartData, stabilizationPeriodSec, diaperModel, changeNeededPercent]);

    // Filter chart data based on brush selection (for main chart display)
    const chartData = useMemo<DataPoint[]>(() => {
      if (!brushSelection || (brushSelection.startTime === null && brushSelection.endTime === null)) {
        return allChartData; // No brush selection, show all
      }

      const startTime = brushSelection.startTime || allChartData[0]?.timestamp;
      const endTime = brushSelection.endTime || allChartData[allChartData.length - 1]?.timestamp;

      return allChartData.filter((d) => {
        return d.timestamp >= startTime && d.timestamp <= endTime;
      });
    }, [allChartData, brushSelection]);

    // Calculate cumulative expected liquid volume from manual additions
    const liquidVolumeData = useMemo(() => {
      if (liquidAdditions.length === 0 || chartData.length === 0) return [];
      
      const firstTimestamp = chartData[0].timestamp.getTime();
      const lastTimestamp = chartData[chartData.length - 1].timestamp.getTime();
      
      // Create a function to calculate cumulative volume at any given time
      const getCumulativeVolumeAtTime = (targetTime: number): number => {
        let total = 0;
        
        liquidAdditions.forEach((addition) => {
          const startTime = new Date(addition.timestamp).getTime();
          const endTime = startTime + addition.duration * 1000;
          
          if (targetTime < startTime) {
            // Addition hasn't started yet
            return;
          } else if (targetTime >= endTime) {
            // Addition is complete
            total += addition.volume;
          } else {
            // Addition is in progress - calculate partial volume
            const elapsedMs = targetTime - startTime;
            const volumePerMs = addition.volume / (addition.duration * 1000);
            total += elapsedMs * volumePerMs;
          }
        });
        
        return total;
      };
      
      // Sample the cumulative volume at various time points
      const samplePoints: { timestamp: Date; volume: number }[] = [];
      const timeStep = Math.max(1000, (lastTimestamp - firstTimestamp) / 500); // Sample every ~second or ~500 points max
      
      for (let t = firstTimestamp; t <= lastTimestamp; t += timeStep) {
        const volume = getCumulativeVolumeAtTime(t);
        samplePoints.push({ timestamp: new Date(t), volume });
      }
      
      // Always include the last point
      if (samplePoints.length === 0 || samplePoints[samplePoints.length - 1].timestamp.getTime() < lastTimestamp) {
        samplePoints.push({ timestamp: new Date(lastTimestamp), volume: getCumulativeVolumeAtTime(lastTimestamp) });
      }
      
      return samplePoints;
    }, [liquidAdditions, chartData]);

    // Filter valid data points for line graphs (status OK/Wet/Leak/Pooling)
    const validWetnessData = useMemo(() => {
      return chartData.filter(d => {
        return (d.statusCode === 0 || d.statusCode === 3 || d.statusCode === 4 || d.statusCode === 5 || d.statusCode === 30);
      });
    }, [chartData]);

    // Scales
    const xScale = useMemo(() => {
      if (chartData.length === 0) return scaleTime({ domain: [new Date(), new Date()], range: [0, innerWidth] });
      
      const times = chartData.map((d) => d.timestamp.getTime());
      const minTime = Math.min(...times);
      const maxTime = Math.max(...times);
      
      // Add padding to the time range
      const padding = (maxTime - minTime) * 0.05 || 60000; // 5% padding or 1 minute minimum
      
      return scaleTime({
        domain: [new Date(minTime - padding), new Date(maxTime + padding)],
        range: [0, innerWidth],
      });
    }, [chartData, innerWidth]);

    const yScaleWetness = useMemo(() => {
      // Dynamic range: cap at leak threshold if enabled
      let maxWetness = 32767;
      if (useDynamicRange && diaperModel?.leakThreshold?.wetnessLevel) {
        maxWetness = Math.min(32767, diaperModel.leakThreshold.wetnessLevel + 1000); // Add small padding
      }
      
      return scaleLinear<number>({
        domain: [0, maxWetness],
        range: [innerHeight, 0],
      });
    }, [innerHeight, useDynamicRange, diaperModel?.leakThreshold?.wetnessLevel]);

    const yScaleVolume = useMemo(() => {
      const maxVolume = diaperModel?.capacity || 1500;
      return scaleLinear<number>({
        domain: [0, maxVolume],
        range: [innerHeight, 0],
      });
    }, [innerHeight, diaperModel?.capacity]);

    // Brush scales (for overview - shows all data)
    const brushXScale = useMemo(() => {
      if (data.length === 0) {
        return scaleTime({ domain: [new Date(), new Date()], range: [0, brushInnerWidth] });
      }
      
      const times = data.map((d) => new Date(d.timestamp).getTime());
      const minTime = Math.min(...times);
      const maxTime = Math.max(...times);
      const padding = (maxTime - minTime) * 0.02 || 30000;
      
      return scaleTime({
        domain: [new Date(minTime - padding), new Date(maxTime + padding)],
        range: [0, brushInnerWidth],
      });
    }, [data, brushInnerWidth]);

    const brushYScale = useMemo(() => {
      if (data.length === 0) return scaleLinear({ domain: [0, 32767], range: [brushInnerHeight, 0] });
      
      const wetnessValues = data.map(d => d.parsed.wetnessLevel);
      const maxWetness = Math.max(...wetnessValues, 1000);
      
      return scaleLinear<number>({
        domain: [0, maxWetness],
        range: [brushInnerHeight, 0],
      });
    }, [data, brushInnerHeight]);

    // Update brush programmatically when brushSelection changes (e.g., via zoom buttons)
    React.useEffect(() => {
      if (skipNextBrushSync.current) {
        skipNextBrushSync.current = false;
        return;
      }

      if (!brushRef.current || data.length === 0) return;
      
      isUpdatingBrushProgrammatically.current = true;
      
      if (!brushSelection || (brushSelection.startTime === null && brushSelection.endTime === null)) {
        brushRef.current.reset();
        isUpdatingBrushProgrammatically.current = false;
        return;
      }
      
      const allDataStart = new Date(data[0].timestamp);
      const allDataEnd = new Date(data[data.length - 1].timestamp);
      const startTime = brushSelection.startTime || allDataStart;
      const endTime = brushSelection.endTime || allDataEnd;
      
      const x0 = brushXScale(startTime);
      const x1 = brushXScale(endTime);

      if (!Number.isFinite(x0) || !Number.isFinite(x1)) {
        isUpdatingBrushProgrammatically.current = false;
        return;
      }
      
      const boundedX0 = Math.min(Math.max(x0, 0), brushInnerWidth);
      const boundedX1 = Math.min(Math.max(x1, 0), brushInnerWidth);

      brushRef.current.updateBrush((prevBrush) => {
        return {
          ...prevBrush,
          start: { x: boundedX0, y: 0 },
          end: { x: boundedX1, y: brushInnerHeight },
          extent: {
            x0: boundedX0,
            x1: boundedX1,
            y0: 0,
            y1: brushInnerHeight,
          },
        };
      });
      
      setTimeout(() => {
        isUpdatingBrushProgrammatically.current = false;
      }, 0);
    }, [brushSelection, data, brushXScale, brushInnerHeight, brushInnerWidth]);

    // Handle brush change from user interaction
    const handleBrushChange = useCallback((domain: Bounds | null) => {
      if (isUpdatingBrushProgrammatically.current || !domain || !onBrushChange) return;

      const { x0, x1 } = domain;

      if (!Number.isFinite(x0) || !Number.isFinite(x1)) return;

      // The Brush component may pass either pixel coordinates or timestamp values
      // Timestamps are much larger (> 1 trillion milliseconds since epoch)
      const looksLikeTimestamp = x0 > 1000000000000 || x1 > 1000000000000;
      
      let startTime: Date;
      let endTime: Date;
      
      if (looksLikeTimestamp) {
        startTime = new Date(Math.min(x0, x1));
        endTime = new Date(Math.max(x0, x1));
      } else {
        const clampedX0 = Math.min(x0, x1);
        const clampedX1 = Math.max(x0, x1);
        const boundedX0 = Math.min(Math.max(clampedX0, 0), brushInnerWidth);
        const boundedX1 = Math.min(Math.max(clampedX1, 0), brushInnerWidth);
        startTime = brushXScale.invert(boundedX0);
        endTime = brushXScale.invert(boundedX1);
      }

      skipNextBrushSync.current = true;
      onBrushChange(startTime, endTime);
    }, [brushInnerWidth, brushXScale, onBrushChange]);

    // Handle mouse move for tooltip
    const handleMouseMove = useCallback(
      (event: React.MouseEvent<SVGRectElement>) => {
        if (chartData.length === 0) return;

        const point = localPoint(event);
        if (!point) return;

        const x = point.x - margin.left;
        const time = xScale.invert(x);

        // Find closest data point
        let closestPoint = chartData[0];
        let minDistance = Math.abs(chartData[0].timestamp.getTime() - time.getTime());

        for (const dataPoint of chartData) {
          const distance = Math.abs(dataPoint.timestamp.getTime() - time.getTime());
          if (distance < minDistance) {
            minDistance = distance;
            closestPoint = dataPoint;
          }
        }

        setHoveredPoint(closestPoint);
        let yPos = yScaleWetness(closestPoint.wetnessLevel);
        yPos = Math.max(0, Math.min(innerHeight, yPos));
        
        const tooltipLeft = xScale(closestPoint.timestamp) + margin.left;
        const tooltipTop = yPos + margin.top;
        
        showTooltip({
          tooltipData: closestPoint,
          tooltipLeft,
          tooltipTop,
        });
        const closestSourceTimestamp = chartData.find((d) => d.timestamp.getTime() === closestPoint.timestamp.getTime())?.sourceTimestamp;
        if (closestSourceTimestamp && onHoverMeasurement && closestSourceTimestamp !== hoveredTimestamp) {
          onHoverMeasurement(closestSourceTimestamp);
        }
      },
      [chartData, xScale, showTooltip, margin.left, margin.top, yScaleWetness, onHoverMeasurement, hoveredTimestamp, innerHeight]
    );

    const handleMouseLeave = useCallback(() => {
      setHoveredPoint(null);
      hideTooltip();
      onLeaveHover?.();
    }, [hideTooltip, onLeaveHover]);

    // Export as PNG
    const exportAsPNG = useCallback(async () => {
      if (!chartRef.current) return;
      try {
        const dataUrl = await toPng(chartRef.current as HTMLElement, {
          cacheBust: true,
          backgroundColor: '#ffffff',
        });
        const link = document.createElement('a');
        link.download = `clip-timeline-${Date.now()}.png`;
        link.href = dataUrl;
        link.click();
      } catch (err) {
        console.error('Export failed:', err);
      }
    }, []);

    useLayoutEffect(() => {
      if (!onMeasureOverhead) return;
      const containerHeight = containerRef.current?.offsetHeight ?? 0;
      const chartHeight = chartRef.current?.offsetHeight ?? 0;
      const overhead = Math.max(0, containerHeight - chartHeight);
      onMeasureOverhead(overhead);
    }, [onMeasureOverhead, chartData.length, parentWidth, parentHeight, changeNeededPercent, diaperModel?.code, diaperModel?.name]);

    const effectiveHoveredPoint = useMemo(() => {
      if (hoveredPoint) return hoveredPoint;
      if (!hoveredTimestamp) return null;
      const match = chartData.find((point) => point.sourceTimestamp === hoveredTimestamp);
      return match ?? null;
    }, [hoveredPoint, hoveredTimestamp, chartData]);

    // Show tooltip for external hover (from other charts)
    React.useEffect(() => {
      if (effectiveHoveredPoint && !hoveredPoint && hoveredTimestamp) {
        let yPos = yScaleWetness(effectiveHoveredPoint.wetnessLevel);
        yPos = Math.max(0, Math.min(innerHeight, yPos));
        
        showTooltip({
          tooltipData: effectiveHoveredPoint,
          tooltipLeft: xScale(effectiveHoveredPoint.timestamp) + margin.left,
          tooltipTop: yPos + margin.top,
        });
      } else if (!hoveredTimestamp && !hoveredPoint) {
        hideTooltip();
      }
    }, [
      effectiveHoveredPoint,
      hoveredPoint,
      hoveredTimestamp,
      hideTooltip,
      margin.left,
      margin.top,
      showTooltip,
      xScale,
      yScaleWetness,
      innerHeight,
    ]);

    if (data.length === 0) {
      return (
        <div className="bg-gray-100 rounded-lg p-8 text-center text-gray-600">
          <p className="text-4xl mb-2">📈</p>
          <p>No data to display</p>
          <p className="text-sm mt-2">Select a clip to view its measurement timeline</p>
        </div>
      );
    }

    return (
      <div className="relative space-y-4" ref={containerRef}>
        {/* Control Panel */}
        <div className="flex flex-wrap gap-4 items-center justify-between bg-gray-50 p-3 rounded-lg border border-gray-200">
          <div className="flex gap-2">
            <button
              onClick={exportAsPNG}
              className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm font-medium"
              title="Export as PNG"
            >
              📸 Export PNG
            </button>
          </div>
          <div className="text-sm text-gray-600">
            {chartData.length} measurements
            {diaperModel && ` • Diaper: ${diaperModel.name}`}
          </div>
        </div>

        {/* Main Chart */}
        <div ref={chartRef}>
          <svg
            width={width}
            height={height}
            className="bg-white border border-gray-200 rounded-lg shadow-sm"
          >
            <Group left={margin.left} top={margin.top}>
              {/* Title */}
              <text
                x={innerWidth / 2}
                y={-20}
                fontSize={14}
                fill="#1D407B"
                textAnchor="middle"
                fontWeight={700}
              >
                Clip Measurement Timeline
              </text>

              {/* Grid */}
              <GridRows
                scale={yScaleWetness}
                width={innerWidth}
                height={innerHeight}
                stroke="#e9ecef"
                strokeWidth={1}
                numTicks={5}
              />

              {/* Red horizontal line - Leak threshold on wetness level */}
              {diaperModel?.leakThreshold?.wetnessLevel && (
                <line
                  x1={0}
                  x2={innerWidth}
                  y1={yScaleWetness(diaperModel.leakThreshold.wetnessLevel)}
                  y2={yScaleWetness(diaperModel.leakThreshold.wetnessLevel)}
                  stroke="#dc2626"
                  strokeWidth={2}
                  strokeDasharray="4,4"
                  opacity={0.7}
                />
              )}

              {/* Yellow horizontal line - Change needed threshold (from criterion/percentage) */}
              {diaperModel && changeNeededPercent && (
                <line
                  x1={0}
                  x2={innerWidth}
                  y1={yScaleVolume(diaperModel.capacity * (changeNeededPercent / 100))}
                  y2={yScaleVolume(diaperModel.capacity * (changeNeededPercent / 100))}
                  stroke="#eab308"
                  strokeWidth={2}
                  strokeDasharray="4,4"
                  opacity={0.7}
                />
              )}

              {/* Alarm event markers -- vertical lines with staggered labels */}
              {(() => {
                const LABEL_ROW_HEIGHT = 14;
                const BASE_Y = -12;
                const visible = alarmEvents
                  .map((alarm, origIdx) => ({ alarm, origIdx, x: xScale(alarm.timestamp) }))
                  .filter(a => a.x >= 0 && a.x <= innerWidth)
                  .sort((a, b) => a.x - b.x);

                return visible.map((item, visIdx) => {
                  const { alarm, x } = item;
                  const color = alarm.hypothetical ? '#f59e0b' : '#dc2626';
                  const label = alarm.hypothetical ? `${alarm.reason} (if enabled)` : alarm.reason;
                  const labelY = BASE_Y - visIdx * LABEL_ROW_HEIGHT;
                  const triangleY = BASE_Y + 2;

                  return (
                    <Group key={`alarm-${item.origIdx}`}>
                      <line
                        x1={x}
                        x2={x}
                        y1={0}
                        y2={innerHeight}
                        stroke={color}
                        strokeWidth={alarm.hypothetical ? 1 : 1.5}
                        strokeDasharray={alarm.hypothetical ? '4,4' : '6,3'}
                        opacity={alarm.hypothetical ? 0.6 : 0.8}
                      />
                      <polygon
                        points={`${x},0 ${x - 4},${triangleY} ${x + 4},${triangleY}`}
                        fill={color}
                        opacity={alarm.hypothetical ? 0.6 : 0.9}
                      />
                      <line
                        x1={x}
                        x2={x}
                        y1={triangleY}
                        y2={labelY + 4}
                        stroke={color}
                        strokeWidth={0.75}
                        opacity={0.5}
                      />
                      <text
                        x={x + 4}
                        y={labelY}
                        fontSize={9}
                        fill={color}
                        fontWeight="bold"
                      >
                        {label}
                      </text>
                    </Group>
                  );
                });
              })()}

              {/* Backend event markers -- indigo alert lines and teal emiction diamonds */}
              {backendEvents.length > 0 && (() => {
                const LABEL_ROW_HEIGHT = 12;
                const beAlerts = backendEvents.filter(e => e.type !== 'emiction');
                const beEmictions = backendEvents.filter(e => e.type === 'emiction');

                const visibleAlerts = beAlerts
                  .map((evt, origIdx) => ({ evt, origIdx, x: xScale(evt.timestamp) }))
                  .filter(a => a.x >= 0 && a.x <= innerWidth)
                  .sort((a, b) => a.x - b.x);

                const visibleEmictions = beEmictions
                  .map((evt, origIdx) => ({ evt, origIdx, x: xScale(evt.timestamp) }))
                  .filter(a => a.x >= 0 && a.x <= innerWidth);

                return (
                  <>
                    {visibleAlerts.map((item, visIdx) => {
                      const { evt, x } = item;
                      const labelY = innerHeight + 14 + visIdx * LABEL_ROW_HEIGHT;
                      return (
                        <Group key={`be-alert-${item.origIdx}`}>
                          <line
                            x1={x} x2={x} y1={0} y2={innerHeight}
                            stroke="#6366f1" strokeWidth={1.5}
                            strokeDasharray="2,3,6,3" opacity={0.7}
                          />
                          <circle cx={x} cy={0} r={3} fill="#6366f1" opacity={0.8} />
                          <text x={x + 4} y={labelY} fontSize={8} fill="#6366f1" fontWeight="600">
                            BE: {evt.label}
                          </text>
                        </Group>
                      );
                    })}
                    {visibleEmictions.map((item) => {
                      const { evt, x } = item;
                      const yVal = validWetnessData.length > 0
                        ? (() => {
                            let closest = validWetnessData[0];
                            let minDiff = Math.abs(closest.timestamp.getTime() - evt.timestamp.getTime());
                            for (const d of validWetnessData) {
                              const diff = Math.abs(d.timestamp.getTime() - evt.timestamp.getTime());
                              if (diff < minDiff) { minDiff = diff; closest = d; }
                            }
                            return yScaleWetness(closest.wetnessLevel);
                          })()
                        : innerHeight / 2;
                      return (
                        <Group key={`be-emiction-${item.origIdx}`}>
                          <polygon
                            points={`${x},${yVal - 6} ${x + 5},${yVal} ${x},${yVal + 6} ${x - 5},${yVal}`}
                            fill="#0d9488" stroke="#fff" strokeWidth={1} opacity={0.85}
                          />
                          {evt.detail && (
                            <text x={x + 7} y={yVal + 3} fontSize={8} fill="#0d9488" fontWeight="600">
                              {evt.detail}
                            </text>
                          )}
                        </Group>
                      );
                    })}
                  </>
                );
              })()}

              {/* Gray line - Raw wetness level (0-32767) - only valid measurements */}
              {validWetnessData.length > 0 && (
                <LinePath
                  data={validWetnessData}
                  x={(d) => xScale(d.timestamp)}
                  y={(d) => yScaleWetness(d.wetnessLevel)}
                  stroke="#6b7280"
                  strokeWidth={2}
                  curve={curveMonotoneX}
                />
              )}

              {/* Blue line - Volume in mL (after 3rd degree polynomial) - only valid measurements */}
              {diaperModel && validWetnessData.length > 0 && (
                <LinePath
                  data={validWetnessData}
                  x={(d) => xScale(d.timestamp)}
                  y={(d) => yScaleVolume(d.volume)}
                  stroke="#3b82f6"
                  strokeWidth={2.5}
                  curve={curveMonotoneX}
                />
              )}

              {/* Pattern definition for liquid addition area */}
              <defs>
                <pattern id="liquidPattern" width="8" height="8" patternUnits="userSpaceOnUse">
                  <rect width="8" height="8" fill={LIQUID_PATTERN_FILL} opacity="0.3" />
                  <path d="M-1,1 l2,-2 M0,8 l8,-8 M7,9 l2,-2" stroke={LIQUID_EXPECTED_COLOR} strokeWidth="1" opacity="0.5" />
                </pattern>
              </defs>

              {/* Light blue striped area under expected liquid volume */}
              {diaperModel && liquidVolumeData.length > 0 && (
                <AreaClosed
                  data={liquidVolumeData}
                  x={(d) => xScale(d.timestamp)}
                  y={(d) => yScaleVolume(d.volume)}
                  yScale={yScaleVolume}
                  fill="url(#liquidPattern)"
                  curve={curveMonotoneX}
                />
              )}

              {/* Blue dashed line - Expected cumulative liquid volume from manual additions */}
              {diaperModel && liquidVolumeData.length > 0 && (
                <LinePath
                  data={liquidVolumeData}
                  x={(d) => xScale(d.timestamp)}
                  y={(d) => yScaleVolume(d.volume)}
                  stroke={LIQUID_EXPECTED_COLOR}
                  strokeWidth={2.5}
                  strokeDasharray={LIQUID_DASH_ARRAY}
                  curve={curveMonotoneX}
                  opacity={0.9}
                />
              )}
              

              {/* Data points - dots for normal, × at correct Y for pooling, × at bottom for errors */}
              {chartData.map((d, i) => {
                const isNormalWetness = (d.statusCode === 0 || d.statusCode === 3 || d.statusCode === 4 || d.statusCode === 5);
                const isPooling = d.statusCode === 30;
                const isHovered = (effectiveHoveredPoint && effectiveHoveredPoint.sourceTimestamp === d.sourceTimestamp) || hoveredTimestamp === d.sourceTimestamp;
                
                if (isNormalWetness) {
                  return (
                    <Group key={i}>
                      <Circle
                        cx={xScale(d.timestamp)}
                        cy={yScaleWetness(d.wetnessLevel)}
                        r={isHovered ? 6 : 4}
                        fill="#6b7280"
                        stroke={isHovered ? '#1D4ED8' : 'white'}
                        strokeWidth={isHovered ? 2 : 1}
                      />
                      {diaperModel && (
                        <Circle
                          cx={xScale(d.timestamp)}
                          cy={yScaleVolume(d.volume)}
                          r={isHovered ? 6 : 4}
                          fill="#3b82f6"
                          stroke={isHovered ? '#1D4ED8' : 'white'}
                          strokeWidth={isHovered ? 2 : 1}
                        />
                      )}
                    </Group>
                  );
                } else if (isPooling) {
                  return (
                    <Group key={i}>
                      <text
                        x={xScale(d.timestamp)}
                        y={yScaleWetness(d.wetnessLevel) + 5}
                        textAnchor="middle"
                        fontWeight="bold"
                        fontSize={isHovered ? 18 : 14}
                        fill="#f97316"
                        stroke={isHovered ? '#1D4ED8' : 'white'}
                        strokeWidth={isHovered ? 1.5 : 0.5}
                      >
                        ×
                      </text>
                      {diaperModel && (
                        <text
                          x={xScale(d.timestamp)}
                          y={yScaleVolume(d.volume) + 5}
                          textAnchor="middle"
                          fontWeight="bold"
                          fontSize={isHovered ? 18 : 14}
                          fill="#3b82f6"
                          stroke={isHovered ? '#1D4ED8' : 'white'}
                          strokeWidth={isHovered ? 1.5 : 0.5}
                        >
                          ×
                        </text>
                      )}
                    </Group>
                  );
                } else {
                  return (
                    <Group key={i}>
                      <text
                        x={xScale(d.timestamp)}
                        y={innerHeight - 5}
                        textAnchor="middle"
                        fontWeight="bold"
                        fontSize={isHovered ? 20 : 16}
                        fill="#dc2626"
                        stroke={isHovered ? '#1D4ED8' : 'white'}
                        strokeWidth={isHovered ? 1.5 : 0.5}
                      >
                        ×
                      </text>
                    </Group>
                  );
                }
              })}

              {/* Hovered point highlight */}
              {effectiveHoveredPoint && (
                <Group>
                  <Circle
                    cx={xScale(effectiveHoveredPoint.timestamp)}
                    cy={yScaleWetness(effectiveHoveredPoint.wetnessLevel)}
                    r={6}
                    fill={StatusColors[effectiveHoveredPoint.statusCode] || UNKNOWN_STATUS_COLOR}
                    opacity={0.5}
                  />
                  <line
                    x1={xScale(effectiveHoveredPoint.timestamp)}
                    x2={xScale(effectiveHoveredPoint.timestamp)}
                    y1={0}
                    y2={innerHeight}
                    stroke="#6366f1"
                    strokeWidth={2}
                    strokeDasharray="6,4"
                    opacity={0.5}
                  />
                </Group>
              )}

              {/* Interactive overlay */}
              <rect
                x={0}
                y={0}
                width={innerWidth}
                height={innerHeight}
                fill="transparent"
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
                style={{ cursor: 'crosshair' }}
              />
              
              {/* Annotations are managed in the Annotation Panel below the graph */}

              {/* Hover regions for individual liquid additions - rendered on top of everything */}
              {liquidAdditions.map((addition, idx) => {
                const startTime = new Date(addition.timestamp);
                const endTime = new Date(startTime.getTime() + addition.duration * 1000);
                const x1 = xScale(startTime);
                const x2 = xScale(endTime);
                const width = x2 - x1;
                const isHovered = hoveredTimestamp === addition.timestamp;
                
                return (
                  <Group key={`liquid-hover-${idx}`}>
                    {/* Red vertical lines at start and end when hovered */}
                    {isHovered && (
                      <>
                        <line
                          x1={x1}
                          y1={0}
                          x2={x1}
                          y2={innerHeight}
                          stroke="#ef4444"
                          strokeWidth={3}
                          opacity={0.8}
                        />
                        <line
                          x1={x2}
                          y1={0}
                          x2={x2}
                          y2={innerHeight}
                          stroke="#ef4444"
                          strokeWidth={3}
                          opacity={0.8}
                        />
                      </>
                    )}
                    
                    {/* Hover area */}
                    <rect
                      x={x1}
                      y={0}
                      width={width}
                      height={innerHeight}
                      fill={isHovered ? 'rgba(239, 68, 68, 0.15)' : 'transparent'}
                      stroke={isHovered ? '#ef4444' : 'none'}
                      strokeWidth={isHovered ? 3 : 0}
                      opacity={1}
                      style={{ cursor: 'pointer', pointerEvents: 'all' }}
                    onMouseEnter={(event) => {
                      // Smart detection: check if mouse is close to any measurement dot
                      const point = localPoint(event) || { x: 0, y: 0 };
                      const mouseX = point.x - margin.left;
                      const mouseY = point.y - margin.top;
                      
                      // Check if within 15px of any data point
                      const nearMeasurement = chartData.some(d => {
                        const dotX = xScale(d.timestamp);
                        const dotY1 = yScaleWetness(d.wetnessLevel);
                        const dotY2 = diaperModel ? yScaleVolume(d.volume) : null;
                        
                        const dist1 = Math.sqrt(Math.pow(mouseX - dotX, 2) + Math.pow(mouseY - dotY1, 2));
                        const dist2 = dotY2 !== null ? Math.sqrt(Math.pow(mouseX - dotX, 2) + Math.pow(mouseY - dotY2, 2)) : Infinity;
                        
                        return dist1 < 15 || dist2 < 15;
                      });
                      
                      // Only trigger liquid hover if NOT near a measurement
                      if (!nearMeasurement && onHoverMeasurement) {
                        onHoverMeasurement(addition.timestamp);
                      }
                    }}
                    onMouseLeave={onLeaveHover}
                  />
                  </Group>
                );
              })}

              {/* Axes */}
              <AxisLeft
                scale={yScaleWetness}
                numTicks={5}
                stroke="#6b7280"
                tickStroke="#6b7280"
                tickLabelProps={() => ({
                  fill: '#6b7280',
                  fontSize: 11,
                  textAnchor: 'end',
                  dy: '0.33em',
                })}
                label="Wetness Level (0-32767)"
                labelProps={{
                  fill: '#6b7280',
                  fontSize: 12,
                  fontWeight: 600,
                  textAnchor: 'middle',
                }}
              />

              {diaperModel && (
                <AxisRight
                  scale={yScaleVolume}
                  left={innerWidth}
                  numTicks={5}
                  stroke="#3b82f6"
                  tickStroke="#3b82f6"
                  tickLabelProps={() => ({
                    fill: '#3b82f6',
                    fontSize: 11,
                    textAnchor: 'start',
                    dy: '0.33em',
                  })}
                  label="Volume (mL)"
                  labelProps={{
                    fill: '#3b82f6',
                    fontSize: 12,
                    fontWeight: 600,
                    textAnchor: 'middle',
                  }}
                />
              )}

              <AxisBottom
                scale={xScale}
                top={innerHeight}
                numTicks={6}
                stroke="#6c757d"
                tickStroke="#6c757d"
                tickFormat={(value) => format(value as Date, 'HH:mm:ss')}
                tickLabelProps={() => ({
                  fill: '#6c757d',
                  fontSize: 11,
                  textAnchor: 'middle',
                })}
              />

              {/* X-axis label */}
              <text
                x={innerWidth / 2}
                y={innerHeight + 45}
                fontSize={12}
                fill="#6c757d"
                textAnchor="middle"
                fontWeight={500}
              >
                Time
              </text>
            </Group>
          </svg>
        </div>

        {/* Brush Overview Chart */}
        {data.length > 0 && (
          <div className="mt-2">
            <svg width={width} height={brushHeight} className="bg-gray-50 border border-gray-200 rounded">
              <Group left={brushMargin.left} top={brushMargin.top}>
                {/* Mini overview line */}
                <LinePath
                  data={data.map(d => ({
                    timestamp: new Date(d.timestamp),
                    wetness: d.parsed.wetnessLevel,
                  }))}
                  x={(d) => brushXScale(d.timestamp)}
                  y={(d) => brushYScale(d.wetness)}
                  stroke="#94a3b8"
                  strokeWidth={1}
                  curve={curveMonotoneX}
                />
                
                {/* Brush selector */}
                <Brush
                  xScale={brushXScale}
                  yScale={brushYScale}
                  width={brushInnerWidth}
                  height={brushInnerHeight}
                  handleSize={8}
                  innerRef={brushRef}
                  resizeTriggerAreas={['left', 'right']}
                  brushDirection="horizontal"
                  onChange={handleBrushChange}
                  onClick={() => onBrushChange?.(null, null)}
                  selectedBoxStyle={{
                    fill: '#3b82f6',
                    fillOpacity: 0.2,
                    stroke: '#1d4ed8',
                    strokeWidth: 2,
                  }}
                  useWindowMoveEvents
                />
              </Group>
              
              {/* Brush axis */}
              <Group left={brushMargin.left} top={brushMargin.top}>
                <AxisBottom
                  scale={brushXScale}
                  top={brushInnerHeight}
                  numTicks={6}
                  stroke="#94a3b8"
                  tickStroke="#94a3b8"
                  tickFormat={(value) => format(value as Date, 'HH:mm')}
                  tickLabelProps={() => ({
                    fill: '#64748b',
                    fontSize: 9,
                    textAnchor: 'middle',
                  })}
                />
              </Group>
            </svg>
            <p className="text-xs text-gray-500 mt-1 text-center">
              📊 Overview Timeline • Drag to pan, resize edges to zoom • Click to reset
            </p>
          </div>
        )}

        {/* Tooltip for measurements */}
        {tooltipOpen && tooltipData && (
          <TooltipWithBounds key={Math.random()} top={tooltipTop} left={tooltipLeft} style={tooltipStyles}>
            <div>
              <strong>Time:</strong> {format(tooltipData.timestamp, 'HH:mm:ss')}
            </div>
            <div style={{ 
              color: tooltipData.statusCode === 0 ? '#22c55e' : '#fbbf24',
              fontWeight: 'bold'
            }}>
              <strong>Status:</strong> {getStatusLabel(tooltipData.statusCode)}
            </div>
            <div>
              <strong>Wetness:</strong> {tooltipData.wetnessLevel.toLocaleString()}
            </div>
            {diaperModel && (
              <div>
                <strong>Volume:</strong> {tooltipData.volume.toFixed(0)} mL ({Math.round((tooltipData.volume / diaperModel.capacity) * 100)}%)
              </div>
            )}
            <div>
              <strong>Battery:</strong> {tooltipData.batteryPercent}%
            </div>
            <div>
              <strong>ADC:</strong> [{tooltipData.adcValues.join(', ')}]
            </div>
            {/* Stabilization tracking for pooling */}
            {(() => {
              const poolingInfo = stabilizationMap[tooltipData.sourceTimestamp];
              const cnInfo = stabilizationMap[tooltipData.sourceTimestamp + ':cn'];
              if (!poolingInfo && !cnInfo) return null;
              return (
                <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255, 255, 255, 0.2)' }}>
                  {poolingInfo && (
                    <div style={{ marginBottom: cnInfo ? '6px' : 0 }}>
                      <div style={{ fontSize: '11px', fontWeight: 'bold', color: poolingInfo.met ? '#ef4444' : '#fb923c', marginBottom: '2px' }}>
                        Pooling Stabilization:
                      </div>
                      <div style={{ fontSize: '10px' }}>
                        Consecutive: {poolingInfo.consecutiveSec.toFixed(0)}s / {poolingInfo.requiredSec}s
                      </div>
                      <div style={{
                        background: 'rgba(255,255,255,0.15)',
                        borderRadius: '3px',
                        height: '6px',
                        marginTop: '3px',
                        overflow: 'hidden',
                      }}>
                        <div style={{
                          width: `${Math.min(100, (poolingInfo.consecutiveSec / poolingInfo.requiredSec) * 100)}%`,
                          height: '100%',
                          background: poolingInfo.met ? '#ef4444' : '#fb923c',
                          borderRadius: '3px',
                          transition: 'width 0.1s',
                        }} />
                      </div>
                      <div style={{ fontSize: '10px', marginTop: '2px', opacity: 0.85 }}>
                        {poolingInfo.met
                          ? 'ALARM TRIGGERED'
                          : `Need ${(poolingInfo.requiredSec - poolingInfo.consecutiveSec).toFixed(0)}s more continuous pooling to trigger alarm`}
                      </div>
                    </div>
                  )}
                  {cnInfo && (
                    <div>
                      <div style={{ fontSize: '11px', fontWeight: 'bold', color: cnInfo.met ? '#ef4444' : '#eab308', marginBottom: '2px' }}>
                        Change Needed Stabilization:
                      </div>
                      <div style={{ fontSize: '10px' }}>
                        Above threshold: {cnInfo.consecutiveSec.toFixed(0)}s / {cnInfo.requiredSec}s
                      </div>
                      <div style={{
                        background: 'rgba(255,255,255,0.15)',
                        borderRadius: '3px',
                        height: '6px',
                        marginTop: '3px',
                        overflow: 'hidden',
                      }}>
                        <div style={{
                          width: `${Math.min(100, (cnInfo.consecutiveSec / cnInfo.requiredSec) * 100)}%`,
                          height: '100%',
                          background: cnInfo.met ? '#ef4444' : '#eab308',
                          borderRadius: '3px',
                        }} />
                      </div>
                      <div style={{ fontSize: '10px', marginTop: '2px', opacity: 0.85 }}>
                        {cnInfo.met
                          ? 'CHANGE NEEDED ALARM TRIGGERED'
                          : `Need ${(cnInfo.requiredSec - cnInfo.consecutiveSec).toFixed(0)}s more to trigger`}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
            {(() => {
              const slope = calculatePolynomialSlope(tooltipData.wetnessLevel, diaperModel);
              if (slope !== null) {
                return (
                  <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255, 255, 255, 0.2)' }}>
                    <div style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '4px' }}>
                      Polynomial Effect:
                    </div>
                    <div style={{ fontSize: '10px' }}>
                      <strong>Slope:</strong> {slope.toFixed(6)} mL/unit
                    </div>
                    <div style={{ fontSize: '10px', opacity: 0.85 }}>
                      {slope > 0 ? '📈' : '📉'} {Math.abs(slope * 100).toFixed(4)} mL per 100 units
                    </div>
                  </div>
                );
              }
              return null;
            })()}
          </TooltipWithBounds>
        )}
        
        {/* Annotations are rendered as visual-only markers on the chart */}

        {/* Tooltip for liquid additions */}
        {hoveredTimestamp && liquidAdditions.length > 0 && (() => {
          const hoveredAddition = liquidAdditions.find(add => add.timestamp === hoveredTimestamp);
          if (!hoveredAddition) return null;
          
          const flowRate = hoveredAddition.volume / hoveredAddition.duration;
          const startTime = new Date(hoveredAddition.timestamp);
          const x = xScale(startTime);
          
          return (
            <TooltipWithBounds 
              key="liquid-tooltip" 
              top={50} 
              left={x + margin.left} 
              style={{...tooltipStyles, backgroundColor: 'rgba(59, 130, 246, 0.95)'}}
            >
              <div style={{ color: '#fff' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '4px', color: '#bfdbfe' }}>
                  💧 Manual Liquid Addition
                </div>
                <div>
                  <strong>Time:</strong> {format(startTime, 'HH:mm:ss')}
                </div>
                <div>
                  <strong>Volume:</strong> {hoveredAddition.volume} mL
                </div>
                <div>
                  <strong>Duration:</strong> {hoveredAddition.duration}s
                </div>
                <div>
                  <strong>Flow Rate:</strong> {flowRate.toFixed(1)} mL/s
                </div>
                <div>
                  <strong>Location:</strong> {hoveredAddition.location}
                </div>
                {hoveredAddition.diaperCounter !== undefined && (
                  <div>
                    <strong>Diaper:</strong> #{hoveredAddition.diaperCounter}
                  </div>
                )}
              </div>
            </TooltipWithBounds>
          );
        })()}

        {/* Legend */}
        <div className="flex flex-wrap gap-4 text-xs text-gray-600 pt-2 border-t border-gray-200">
          <div className="flex items-center gap-1">
            <div className="w-4 h-0.5 bg-gray-500"></div>
            <span>Wetness Level (Left, 0-32767)</span>
          </div>
          {diaperModel && (
            <>
              <div className="flex items-center gap-1">
                <div className="w-4 h-0.5 bg-blue-500"></div>
                <span>Volume mL (Right)</span>
              </div>
              {liquidAdditions.length > 0 && (
                <div className="flex items-center gap-1">
                  <div
                    className="w-4 h-px border-t-2 border-dashed"
                    style={{ borderColor: LIQUID_EXPECTED_COLOR }}
                  ></div>
                  <span>Expected Volume (Manual Additions)</span>
                </div>
              )}
              {diaperModel.leakThreshold?.wetnessLevel && (
                <div className="flex items-center gap-1">
                  <div className="w-4 h-px border-t-2 border-dashed border-red-600"></div>
                  <span>Leak Threshold ({diaperModel.leakThreshold.wetnessLevel.toLocaleString()})</span>
                </div>
              )}
              <div className="flex items-center gap-1">
                <div className="w-4 h-px border-t-2 border-dashed border-yellow-500"></div>
                <span>Change Needed ({changeNeededPercent}%)</span>
              </div>
            </>
          )}
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-green-500"></div>
            <span>Status: Normal (dots on line)</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-lg font-bold" style={{ color: '#f97316' }}>×</span>
            <span>Pooling (on graph)</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-lg font-bold text-red-600">×</span>
            <span>Detached/Error (bottom)</span>
          </div>
          
        </div>
      </div>
    );
  }
);

ClipTimelineChart.displayName = 'ClipTimelineChart';

// Main responsive wrapper
export const ClipTimeline: React.FC<ClipTimelineProps> = (props) => {
  const [, setOverhead] = useState(0);
  const overheadRef = useRef(0);
  const hoveredMeasurementTimestamp = useClipDataStore((state: any) => state.hoveredMeasurementTimestamp);
  const setHoveredMeasurement = useClipDataStore((state: any) => state.setHoveredMeasurement);

  const handleOverheadChange = useCallback((value: number) => {
    const normalized = Math.max(0, Math.round(value));
    if (Math.abs(normalized - overheadRef.current) > 2) {
      overheadRef.current = normalized;
      setOverhead(normalized);
    }
  }, []);

  const handleHover = useCallback((timestamp: string) => {
    setHoveredMeasurement(timestamp);
  }, [setHoveredMeasurement]);

  const handleHoverEnd = useCallback(() => {
    setHoveredMeasurement(null);
  }, [setHoveredMeasurement]);

  return (
    <div className="h-full min-h-[600px]">
      <ParentSize>
        {({ width, height }) => {
          const safeWidth = width && width > 0 ? width : 800;
          const availableHeight = height && height > 0 ? height : 600;
          const safeChartHeight = Math.max(360, availableHeight - overheadRef.current);

          return (
            <ClipTimelineChart
              {...props}
              width={safeWidth}
              height={safeChartHeight}
              onMeasureOverhead={handleOverheadChange}
              hoveredTimestamp={hoveredMeasurementTimestamp}
              onHoverMeasurement={handleHover}
              onLeaveHover={handleHoverEnd}
            />
          );
        }}
      </ParentSize>
    </div>
  );
};

